import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send, AlertCircle, Loader2, MessageSquare, Search, X, Phone, Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export default function Messages() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const messagesEndRef = useRef(null);

  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const pollIntervalRef = useRef(null);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fetch conversations
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchConversations = async () => {
      try {
        const response = await fetch(`${API_URL}/api/messages/conversations`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        });

        if (response.ok) {
          const data = await response.json();
          setConversations(data.conversations || []);
        }
      } catch (error) {
        console.error('Failed to fetch conversations:', error);
        toast.error('Failed to load conversations');
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
  }, [isAuthenticated, navigate]);

  // Fetch messages for selected conversation
  useEffect(() => {
    if (!selectedConversation) {
      setMessages([]);
      return;
    }

    const fetchMessages = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/messages/conversation/${selectedConversation.other_user_id}`,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
        );

        if (response.ok) {
          const data = await response.json();
          setMessages(data.messages || []);

          // Mark as read
          await fetch(
            `${API_URL}/api/messages/read/${selectedConversation.other_user_id}`,
            {
              method: 'PUT',
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            }
          );

          // Update conversation unread count
          setConversations(prev =>
            prev.map(conv =>
              conv.other_user_id === selectedConversation.other_user_id
                ? { ...conv, unread_count: 0 }
                : conv
            )
          );
        }
      } catch (error) {
        console.error('Failed to fetch messages:', error);
      }
    };

    fetchMessages();
  }, [selectedConversation]);

  // Poll for new messages every 10 seconds
  useEffect(() => {
    if (!selectedConversation) return;

    pollIntervalRef.current = setInterval(() => {
      const fetchNewMessages = async () => {
        try {
          const response = await fetch(
            `${API_URL}/api/messages/conversation/${selectedConversation.other_user_id}`,
            { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
          );

          if (response.ok) {
            const data = await response.json();
            setMessages(data.messages || []);
          }
        } catch (error) {
          console.error('Failed to poll messages:', error);
        }
      };

      fetchNewMessages();
    }, 10000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [selectedConversation]);

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!messageInput.trim() || !selectedConversation) {
      return;
    }

    setSendingMessage(true);
    try {
      const response = await fetch(`${API_URL}/api/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          recipient_id: selectedConversation.other_user_id,
          content: messageInput,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send message');
      }

      const newMessage = await response.json();
      setMessages([...messages, newMessage]);
      setMessageInput('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(error.message);
    } finally {
      setSendingMessage(false);
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.other_user_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-20 pb-12">
          <div className="max-w-6xl mx-auto px-6">
            <Skeleton className="h-12 w-48 mb-8" />
            <div className="grid md:grid-cols-3 gap-6">
              <Card>
                <CardContent className="p-6">
                  {[1, 2, 3].map(i => (
                    <Skeleton key={i} className="h-16 mb-4" />
                  ))}
                </CardContent>
              </Card>
              <Card className="md:col-span-2">
                <CardContent className="p-6">
                  <Skeleton className="h-96" />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-20 pb-12">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <AlertCircle className="h-12 w-12 text-amber-600 mx-auto mb-4" />
            <h1 className="text-2xl font-serif font-bold text-slate-900 mb-2">Please Log In</h1>
            <p className="text-slate-600 mb-6">You need to log in to access messages.</p>
            <Button onClick={() => navigate('/login')}>Go to Login</Button>
          </div>
        </div>
      </div>
    );
  }

  // Mobile view - show conversation list or messages
  if (isMobileView) {
    if (!selectedConversation) {
      return (
        <div className="min-h-screen bg-slate-50">
          <Navbar />
          <div className="pt-20 pb-12">
            <div className="max-w-2xl mx-auto px-6">
              <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                <h1 className="font-serif text-3xl text-slate-900 mb-2">Messages</h1>
                <p className="text-slate-600">Start a conversation</p>
              </motion.div>

              <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              <div className="space-y-2">
                {filteredConversations.length > 0 ? (
                  filteredConversations.map(conv => (
                    <motion.button
                      key={conv.other_user_id}
                      onClick={() => setSelectedConversation(conv)}
                      className="w-full text-left"
                      whileHover={{ scale: 1.02 }}
                    >
                      <Card className="hover:shadow-md transition-all">
                        <CardContent className="p-4 flex items-center gap-3">
                          <Avatar>
                            <AvatarImage src={conv.other_user_picture} alt={conv.other_user_name} />
                            <AvatarFallback>{conv.other_user_name?.[0]}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900">{conv.other_user_name}</p>
                              {conv.unread_count > 0 && (
                                <Badge className="bg-accent text-white">{conv.unread_count}</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 truncate">{conv.last_message}</p>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.button>
                  ))
                ) : (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-600">No conversations yet</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Navbar />
        <div className="flex-1 pt-20 pb-4 overflow-hidden">
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="px-6 pb-4 border-b border-slate-200 bg-white">
              <div className="flex items-center gap-3 mb-4">
                <button
                  onClick={() => setSelectedConversation(null)}
                  className="p-1 hover:bg-slate-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
                <div className="flex-1">
                  <h2 className="font-semibold text-slate-900">{selectedConversation.other_user_name}</h2>
                  <p className="text-xs text-slate-500">Active now</p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <AnimatePresence>
                {messages.map((msg, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-xs rounded-lg px-4 py-2 ${
                        msg.sender_id === user.id
                          ? 'bg-accent text-white'
                          : 'bg-slate-100 text-slate-900'
                      }`}
                    >
                      <p className="text-sm">{msg.content}</p>
                      <p className={`text-xs mt-1 ${msg.sender_id === user.id ? 'text-white/70' : 'text-slate-500'}`}>
                        {new Date(msg.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="px-6 py-4 border-t border-slate-200 bg-white">
              <form onSubmit={handleSendMessage} className="flex gap-2">
                <Input
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  placeholder="Type a message..."
                  disabled={sendingMessage}
                  className="flex-1"
                />
                <Button
                  type="submit"
                  disabled={sendingMessage || !messageInput.trim()}
                  size="icon"
                  className="bg-accent hover:bg-accent/90"
                >
                  {sendingMessage ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Desktop view
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="pt-20 pb-12">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <h1 className="font-serif text-3xl md:text-4xl text-slate-900 mb-2">Messages</h1>
            <p className="text-slate-600">Chat with property owners and renters</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 min-h-screen">
            {/* Conversations List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Conversations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 text-sm"
                  />
                </div>

                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredConversations.length > 0 ? (
                    filteredConversations.map(conv => (
                      <motion.button
                        key={conv.other_user_id}
                        onClick={() => setSelectedConversation(conv)}
                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                          selectedConversation?.other_user_id === conv.other_user_id
                            ? 'bg-accent/10 border border-accent'
                            : 'hover:bg-slate-50 border border-transparent'
                        }`}
                        whileHover={{ scale: 1.02 }}
                      >
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={conv.other_user_picture} alt={conv.other_user_name} />
                            <AvatarFallback>{conv.other_user_name?.[0]}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900">{conv.other_user_name}</p>
                            <p className="text-xs text-slate-500 truncate">{conv.last_message}</p>
                          </div>
                          {conv.unread_count > 0 && (
                            <Badge className="bg-accent text-white text-xs">{conv.unread_count}</Badge>
                          )}
                        </div>
                      </motion.button>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <MessageSquare className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-600">No conversations</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Messages View */}
            <Card className="md:col-span-2">
              {selectedConversation ? (
                <div className="h-screen max-h-96 md:max-h-full flex flex-col">
                  {/* Header */}
                  <CardHeader className="border-b border-slate-200">
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarImage src={selectedConversation.other_user_picture} alt={selectedConversation.other_user_name} />
                        <AvatarFallback>{selectedConversation.other_user_name?.[0]}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <CardTitle>{selectedConversation.other_user_name}</CardTitle>
                        <p className="text-xs text-slate-500">Active now</p>
                      </div>
                    </div>
                  </CardHeader>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    <AnimatePresence>
                      {messages.length > 0 ? (
                        messages.map((msg, idx) => (
                          <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-xs rounded-lg px-4 py-2 ${
                                msg.sender_id === user.id
                                  ? 'bg-accent text-white'
                                  : 'bg-slate-100 text-slate-900'
                              }`}
                            >
                              <p className="text-sm">{msg.content}</p>
                              <p className={`text-xs mt-1 ${msg.sender_id === user.id ? 'text-white/70' : 'text-slate-500'}`}>
                                {new Date(msg.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </motion.div>
                        ))
                      ) : (
                        <div className="text-center py-12">
                          <MessageSquare className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                          <p className="text-sm text-slate-600">No messages yet. Start the conversation!</p>
                        </div>
                      )}
                    </AnimatePresence>
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input */}
                  <div className="p-6 border-t border-slate-200">
                    <form onSubmit={handleSendMessage} className="flex gap-2">
                      <Input
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        placeholder="Type a message..."
                        disabled={sendingMessage}
                      />
                      <Button
                        type="submit"
                        disabled={sendingMessage || !messageInput.trim()}
                        size="icon"
                        className="bg-accent hover:bg-accent/90"
                      >
                        {sendingMessage ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                    </form>
                  </div>
                </div>
              ) : (
                <CardContent className="flex items-center justify-center h-96">
                  <div className="text-center">
                    <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">Select a conversation to start messaging</p>
                  </div>
                </CardContent>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
